
import { useRef } from 'react';

const glowKeyframes = `
@keyframes yellowGlow {
  0%, 100% {
    box-shadow: 0 0 4px rgba(212, 175, 55, 0.2), 0 0 8px rgba(212, 175, 55, 0.1);
    background-color: rgba(212, 175, 55, 0.15);
  }
  50% {
    box-shadow: 0 0 12px rgba(212, 175, 55, 0.6), 0 0 24px rgba(212, 175, 55, 0.3);
    background-color: rgba(212, 175, 55, 0.35);
  }
}
`;

interface StepData {
  number: string;
  title: string;
  action: string;
  description: string;
  descriptionList?: string[];
  tip?: string;
  warning?: string;
  icon: string;
  explorerUrl?: string;
  detailImage?: string;
  additionalNote?: string;
  stepList?: Array<{
    title: string;
    items: string[];
    note?: string;
  }>;
  importantNotes?: Array<{
    type: 'warning' | 'info';
    title: string;
    items: string[];
  }>;
  contentBlocks?: Array<{
    title: string;
    content: string;
  }>;
  hideDesktopNode?: boolean;
}

export default function ParticipationGuide() {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const steps: StepData[] = [
    {
      number: '01',
      title: 'TXID 확인하기.',
      action: '먼저 해야 할 일',
      description: "반드시 '종이지갑'을 이용해 참여해 주세요.",
      descriptionList: [
        '모빅 지갑 어플 실행 후 [+] 버튼 클릭',
        '종이지갑의 금박을 벗겨 개인키 스캔',
        '[보내기] 클릭 후 받는 주소에 본인의 종이지갑 공개주소 입력',
        '소량만 셀프전송하기',
        '완료 후 [상세내역] > [세부사항]에서 TXID 복사',
      ],
      additionalNote:
        '💡 상세내역이란? 모빅 지갑 어플에서 전송 완료 후 해당 거래를 클릭하면 나오는 화면입니다. 여기서 [세부사항]을 누르면 TXID를 확인하고 복사할 수 있어요.',
      icon: 'ri-wallet-3-line',
      detailImage:
        'https://static.readdy.ai/image/227cab682d83965c949f1d2451ee4e6c/c0df6a3e6415e47c6c77e577593f1968.jpeg',
    },
    {
      number: '02',
      title: '모빅 볼트등록 방법',
      action: '두 번째 단계',
      description:
        "모비커 '호구호구'님이 만드신 모빅볼트에 접속하셔서 step0 TXID를 입력합니다. 입력 후 공개키 조회하기를 누릅니다. 공개키는 자동 생성되며 3분정도 걸릴 수 있습니다.",
      icon: 'ri-safe-line',
      explorerUrl: 'https://mobickvault.com/',
      stepList: [
        {
          title: 'Step 0: TXID 입력',
          items: [
            "모비커 '호구호구'님이 제작한 모빅볼트 접속 후, 복사해둔 TXID를 입력합니다.",
          ],
        },
        {
          title: 'Step 1: 정보 입력 및 잠금 설정',
          items: [
            "자동 생성된 공개키와 **닉네임 블록**(증명 기간)**을 입력합니다.",
            "설정 범위: 최소 1,000 블록 이상 ~ 65,536 미만 (최대 약 1년 3개월) /(한달 블록 수= 4,300 / 1년 = 52560)",
          ],
          note:
            '⚠️ 매우 중요! (필독)\n\n입력하는 잠금 블록 값에 따라 생성되는 주소가 완전히 달라집니다. 잠금 블록 값을 반드시 종이지갑에 함께 메모해 두시기 바랍니다.',
        },
      ],
    },
    {
      number: '03',
      title: '증명 현황 조희',
      action: '',
      description: '',
      icon: 'ri-search-eye-line',
      hideDesktopNode: true,
      importantNotes: [
        {
          type: 'warning',
          title: '📌 인식 및 입력 조건',
          items: [
            '최소 0.0001 mo 이상부터 인식이 가능합니다.',
            '시스템에서 수량이 정상적으로 확인되어야 닉네임 입력 단계로 진행할 수 있습니다.',
          ],
        },
        {
          type: 'info',
          title: '⚠️ 연습 및 테스트 권장 (필독)',
          items: [
            '큰 금액을 보내기 전, 반드시 0.0001 mo로 충분한 연습과 테스트를 거쳐주세요.',
            '정상 작동을 확인하신 후, 확신이 들 때 실제 락업 이벤트에 참여하시기를 강력히 권장합니다.',
          ],
        },
      ],
    },
    {
      number: '04',
      title: '볼트주소로 락업하기',
      action: '마지막 단계',
      description: '',
      icon: 'ri-file-edit-line',
      hideDesktopNode: true,
      contentBlocks: [
        {
          title: '',
          content: '생성된 볼트 주소로 락업할 수량을 전송합니다.',
        },
        {
          title: '최소 참여 요건',
          content: '참여 최소 수량은 0.01 mo 이상입니다.',
        },
        {
          title: '참여 확정',
          content: '볼트에서 유효 보유량이 정상적으로 확인된 후, 구글 폼을 작성하여 확정해 주세요.',
        },
        {
          title: '',
          content: '설정하신 잠금 블록 수에 도달하면, Step 3(잠금 해제) 과정을 거친 후 자유롭게 출금하시면 됩니다.',
        },
      ],
    },
  ];

  return (
    <section id="guide" className="bg-[#0A1628] py-10 sm:py-24 lg:py-32">
      <style>{glowKeyframes}</style>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Title */}
        <div className="text-center mb-6 sm:mb-16 lg:mb-20">
          <h2 className="text-2xl sm:text-4xl lg:text-6xl font-bold text-white mb-2 sm:mb-6">
            참여 안내
          </h2>
          <div className="flex justify-center">
            <div className="w-12 sm:w-24 lg:w-32 h-0.5 bg-[#D4AF37]"></div>
          </div>
        </div>

        {/* Timeline Steps */}
        <div className="relative">
          {/* Vertical Timeline Line - Desktop */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#D4AF37] via-[#D4AF37]/50 to-[#D4AF37] transform -translate-x-1/2"></div>

          {/* Mobile Timeline Line */}
          <div className="lg:hidden absolute left-[18px] sm:left-[22px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#D4AF37] via-[#D4AF37]/50 to-[#D4AF37]"></div>

          <div className="space-y-8 sm:space-y-12 lg:space-y-0">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`relative lg:flex lg:items-center lg:gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } ${index > 0 ? 'mt-8 sm:mt-12 lg:mt-24' : ''}`}
              >
                {/* Timeline Node - Desktop */}
                {!step.hideDesktopNode && (
                  <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 z-10">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8941F] flex items-center justify-center shadow-lg shadow-[#D4AF37]/30 border-4 border-[#0A1628]">
                      <span className="text-white font-bold text-lg">{step.number}</span>
                    </div>
                  </div>
                )}

                {/* Mobile Timeline Node */}
                <div className="lg:hidden absolute left-0 top-0 z-10">
                  <div className="w-[38px] h-[38px] sm:w-[46px] sm:h-[46px] rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8941F] flex items-center justify-center shadow-lg shadow-[#D4AF37]/30 border-[3px] border-[#0A1628]">
                    <span className="text-white font-bold text-sm sm:text-base">{step.number}</span>
                  </div>
                </div>

                {/* Content Card */}
                <div className={`lg:w-[calc(50% - 3rem)] ${index % 2 === 0 ? 'lg:pr-8' : 'lg:pl-8'} pl-12 sm:pl-14 lg:pl-0`}>
                  <div
                    ref={(el) => {
                      cardRefs.current[index] = el;
                    }}
                    className={`bg-[#1A2332] rounded-xl sm:rounded-2xl p-4 sm:p-8 transition-all duration-300 hover:shadow-2xl hover:shadow-[#D4AF37] group relative ${
                      index % 2 === 0 ? 'lg:ml-auto' : 'lg:mr-auto'
                    }`}
                  >
                    {/* Action Badge */}
                    {step.action && (
                      <div className="mb-2 sm:mb-4">
                        <span
                          className={`inline-block px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-sm font-medium ${
                            step.action === '먼저 해야 할 일'
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                              : step.action === '마지막 단계'
                              ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30'
                              : 'bg-white/10 text-white/70 border border-white/20'
                          }`}
                        >
                          {step.action}
                        </span>
                      </div>
                    )}

                    <div className="flex items-start gap-2.5 sm:gap-4">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors flex-shrink-0">
                        <i className={`${step.icon} text-[#D4AF37] text-lg sm:text-3xl`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap mb-1">
                          <h3 className="text-base sm:text-2xl font-bold text-white">{step.title}</h3>
                          {step.number === '04' && (
                            <span className="inline-block px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-sm font-bold bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/40 whitespace-nowrap">
                              0.01모 이상
                            </span>
                          )}
                        </div>

                        {/* Explorer Link */}
                        {step.explorerUrl && (
                          <div className="mb-2">
                            <a
                              href={step.explorerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 sm:gap-1.5 text-[#D4AF37] hover:text-[#F0D060] text-[10px] sm:text-sm font-medium transition-colors cursor-pointer min-h-[36px] sm:min-h-[auto]"
                            >
                              <i className="ri-lightbulb-line text-xs sm:text-base"></i>
                              <span className="underline underline-offset-2 break-all">{step.explorerUrl}</span>
                              <i className="ri-external-link-line text-[10px] sm:text-sm flex-shrink-0"></i>
                            </a>
                          </div>
                        )}

                        {/* Description - text or list */}
                        {step.descriptionList ? (
                          <div>
                            <p className="text-amber-400 text-xs sm:text-base font-semibold flex items-center gap-1 sm:gap-1.5 mb-2">
                              <i className="ri-alarm-warning-line text-sm sm:text-lg"></i>
                              {step.description}
                            </p>
                            <div className="bg-[#0D1A2D] rounded-lg p-2.5 sm:p-4 border border-white/5">
                              <p className="text-[#D4AF37] text-[10px] sm:text-sm font-bold mb-1.5 sm:mb-2 flex items-center gap-1 sm:gap-1.5">
                                <i className="ri-rocket-line text-xs sm:text-base"></i>
                                TXID(트랜잭션 아이디) 추출 방법
                              </p>
                              <ol className="space-y-1 sm:space-y-1.5">
                                {step.descriptionList.map((item, i) => (
                                  <li
                                    key={i}
                                    className="flex items-start gap-1.5 sm:gap-2 text-[#708090] text-xs sm:text-base leading-relaxed"
                                  >
                                    <span className="text-[#D4AF37] font-bold text-[10px] sm:text-sm mt-0.5 flex-shrink-0">
                                      {i + 1}.
                                    </span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ol>
                            </div>
                            {step.additionalNote && (
                              <div className="mt-2.5 sm:mt-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-2.5 sm:p-4">
                                <p className="text-white/90 text-xs sm:text-base leading-relaxed">{step.additionalNote}</p>
                              </div>
                            )}
                          </div>
                        ) : step.stepList ? (
                          <div className="space-y-2.5 sm:space-y-3">
                            {step.stepList.map((stepItem, i) => (
                              <div key={i} className="bg-[#0D1A2D] rounded-lg p-2.5 sm:p-4 border border-white/5">
                                <p className="text-[#D4AF37] text-[10px] sm:text-sm font-bold mb-1.5 sm:mb-2 flex items-center gap-1 sm:gap-1.5">
                                  <span className="text-sm sm:text-lg">{i + 1}.</span>
                                  {stepItem.title}
                                </p>
                                <ul className="space-y-1 sm:space-y-1.5">
                                  {stepItem.items.map((item, j) => (
                                    <li
                                      key={j}
                                      className="flex items-start gap-1.5 sm:gap-2 text-[#708090] text-xs sm:text-base leading-relaxed"
                                    >
                                      <span className="text-[#D4AF37] mt-0.5 flex-shrink-0">•</span>
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                                {stepItem.note && (
                                  <div className="mt-2 sm:mt-3 bg-amber-500/10 border border-amber-500/25 rounded-lg p-2.5 sm:p-3">
                                    <p className="text-amber-400 text-[10px] sm:text-sm leading-relaxed whitespace-pre-line">
                                      {stepItem.note}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : step.importantNotes ? (
                          <div className="space-y-2.5 sm:space-y-3">
                            {step.importantNotes.map((note, i) => (
                              <div
                                key={i}
                                className={`rounded-lg p-2.5 sm:p-4 border ${
                                  note.type === 'warning' ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/30'
                                }`}
                              >
                                <p
                                  className={`text-xs sm:text-base font-bold mb-1.5 sm:mb-2 ${
                                    note.type === 'warning' ? 'text-red-400' : 'text-amber-400'
                                  }`}
                                >
                                  {note.title}
                                </p>
                                <ul className="space-y-1 sm:space-y-1.5">
                                  {note.items.map((item, j) => (
                                    <li
                                      key={j}
                                      className="flex items-start gap-1.5 sm:gap-2 text-white/80 text-xs sm:text-base leading-relaxed"
                                    >
                                      <span
                                        className={`mt-0.5 flex-shrink-0 ${
                                          note.type === 'warning' ? 'text-red-400' : 'text-amber-400'
                                        }`}
                                      >
                                        •
                                      </span>
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        ) : step.contentBlocks ? (
                          <div className="space-y-2.5 sm:space-y-3">
                            {step.contentBlocks.map((block, i) => (
                              <div key={i} className="bg-[#0D1A2D] rounded-lg p-2.5 sm:p-4 border border-white/5">
                                {block.title && (
                                  <p className="text-[#D4AF37] text-[10px] sm:text-sm font-bold mb-1.5 sm:mb-2">{block.title}</p>
                                )}
                                <p className="text-[#708090] text-xs sm:text-base leading-relaxed">{block.content}</p>
                              </div>
                            ))}
                          </div>
                        ) : step.description ? (
                          <p className="text-[#708090] text-xs sm:text-lg leading-relaxed">{step.description}</p>
                        ) : null}

                        {step.tip && (
                          <p className="mt-2 text-[#D4AF37]/80 text-[10px] sm:text-base flex items-start gap-1 sm:gap-1.5">
                            <i className="ri-information-line mt-0.5 flex-shrink-0 text-xs sm:text-base"></i>
                            <span>{step.tip}</span>
                          </p>
                        )}

                        {/* Detail Image */}
                        {step.detailImage && (
                          <div className="mt-3 sm:mt-4">
                            <div className="rounded-lg overflow-hidden border border-white/10">
                              <img src={step.detailImage} alt="TXID 추출 상세 안내" className="w-full h-auto object-contain" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden lg:block lg:w-[calc(50% - 3rem)]"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Important Notice Section */}
        <div className="mt-8 sm:mt-16 bg-[#0D1A2D] rounded-xl sm:rounded-2xl p-4 sm:p-8 border border-white/10">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-[#D4AF37]"></div>
            <h3 className="text-lg sm:text-2xl font-bold text-white">중요 안내</h3>
          </div>
          <ul className="space-y-2 sm:space-y-3">
            <li className="flex items-start gap-1.5 sm:gap-2 text-white/70 text-xs sm:text-lg leading-relaxed">
              <span className="text-white/50 mt-0.5 sm:mt-1 flex-shrink-0">•</span>
              <span>이벤트 기간: 2026년 2월 19일 ~ 3월 18일락업 기록만 인정 됩니다.)</span>
            </li>
            <li className="flex items-start gap-1.5 sm:gap-2 text-white/70 text-xs sm:text-lg leading-relaxed">
              <span className="text-white/50 mt-0.5 sm:mt-1 flex-shrink-0">•</span>
              <span>참여 최소 요건: 0.01모 이상 / 최소 락업 기간 7일 이상</span>
            </li>
            <li className="flex items-start gap-1.5 sm:gap-2 text-white/70 text-xs sm:text-lg leading-relaxed">
              <span className="text-white/50 mt-0.5 sm:mt-1 flex-shrink-0">•</span>
              <span>신청서 마감: 2026년 3월 18일(수) 23:59 / 최종 스냅샷 블록:31,828 (±100)</span>
            </li>
            <li className="flex items-start gap-1.5 sm:gap-2 text-white/70 text-xs sm:text-lg leading-relaxed">
              <span className="text-white/50 mt-0.5 sm:mt-1 flex-shrink-0">•</span>
              <span>공정한 기회 제공을 위해 1인 1회 수령을 원칙으로 하며, 중복 및 대리 수령 불가합니다.</span>
            </li>
            <li className="flex items-start gap-1.5 sm:gap-2 text-amber-400 text-xs sm:text-lg leading-relaxed">
              <span className="text-amber-400 mt-0.5 sm:mt-1 flex-shrink-0">•</span>
              <span>안내된 가이드를 숙지하지 않아 발생하는 개인키 분실, 블록 수 착오 등의 문제에 대해서는 운영측에서 도움을 드리기 어려우니 각별히 유의해 주시기 바랍니다.</span>
            </li>
          </ul>
        </div>

        {/* Main CTA Button */}
        <div className="text-center mt-8 sm:mt-12">
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSdIusXkn358TUorU5S7lXeUoqcDzAwZU3mCN5otAbCTx0RKuQ/viewform?usp=header"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white px-6 sm:px-12 py-3.5 sm:py-5 rounded-full text-sm sm:text-xl font-bold hover:shadow-2xl hover:shadow-[#D4AF37] transition-all duration-300 hover:-translate-y-1 whitespace-nowrap cursor-pointer min-h-[44px]"
          >
            <i className="ri-edit-line text-lg sm:text-2xl"></i>
            구글 폼 작성하기
          </a>
          <p className="mt-3 sm:mt-4 text-white/50 text-xs sm:text-base">
            위 4단계를 완료한 후 신청서를 제출해주세요
          </p>
        </div>
      </div>
    </section>
  );
}
